/**
 * Unified "+ Add code" entry point under each turn / paragraph in the
 * paused-for-review review UX. Single button → single popover that does
 * BOTH paths in one fluent flow:
 *
 *   1. User types → autocomplete shows every code already in the
 *      codebook that matches the typed text (with the code's definition
 *      below each option so they can tell similar codes apart).
 *   2. Picking an existing code → fires assignCodeToTurn → popover
 *      closes, turn re-renders with the new chip.
 *   3. Typing a name that doesn't match any existing code → the
 *      autocomplete surfaces a "Create new" option at the top. Picking
 *      it expands a "Definition" field (required for user-created codes,
 *      per spec) + a Create button. On submit fires createCodeOnTurn.
 *
 * Single button under each turn keeps the chip row uncluttered while
 * giving researchers a fast path to both the "I already have a code for
 * this" and "this needs a new code" cases.
 */
import { useMemo, useState } from 'react';

import { AddCircleOutline } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Popover,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';

import { useAssignCodeToTurn, useCreateCodeOnTurn } from '@hooks/useCodeReview';

const NEW_OPTION_KEY = '__create_new__';
const filter = createFilterOptions();

/**
 * Build the autocomplete options list from the analysis-item's
 * codes_quotes_dict shape (Map of name → {definition, summary, sources}).
 * Sorted alphabetically so the researcher can scan; the "Create new"
 * option is appended dynamically by filterOptions below when the typed
 * value doesn't match anything.
 */
function buildOptions(codesQuotesDict) {
  if (!codesQuotesDict || typeof codesQuotesDict !== 'object') return [];
  return Object.entries(codesQuotesDict)
    .map(([name, value]) => ({
      name,
      definition: (value && typeof value === 'object' && value.definition) || '',
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export default function AddCodeToTurnPopover({
  studyId,
  analysisItemId,
  qualitativeItemId,
  turnId,
  highlightedText = '',
  codesQuotesDict,
  size = 'small',
}) {
  const assignMutation = useAssignCodeToTurn(studyId, analysisItemId);
  const createMutation = useCreateCodeOnTurn(studyId, analysisItemId);

  const [anchorEl, setAnchorEl] = useState(null);
  const [value, setValue] = useState(null); // selected option object (existing or sentinel)
  const [inputValue, setInputValue] = useState('');
  const [definition, setDefinition] = useState('');

  const options = useMemo(() => buildOptions(codesQuotesDict), [codesQuotesDict]);

  const reset = () => {
    setValue(null);
    setInputValue('');
    setDefinition('');
  };

  const close = () => {
    if (assignMutation.isPending || createMutation.isPending) return;
    setAnchorEl(null);
    reset();
  };

  const isCreateMode = value && value.key === NEW_OPTION_KEY;
  const isExistingPick = value && !value.key;

  const trimmedName = isCreateMode ? (value.inputValue || '').trim() : '';
  const trimmedDefinition = definition.trim();
  const canCreate = isCreateMode && trimmedName.length > 0 && trimmedDefinition.length > 0;
  const canAssign = isExistingPick && value.name;

  const handleAssign = () => {
    if (!canAssign) return;
    assignMutation.mutate(
      {
        qualitativeItemId,
        codeName: value.name,
        turnId,
        highlightedText,
      },
      {
        onSuccess: () => {
          setAnchorEl(null);
          reset();
        },
      },
    );
  };

  const handleCreate = (evt) => {
    evt?.preventDefault?.();
    if (!canCreate) return;
    createMutation.mutate(
      {
        qualitativeItemId,
        codeName: trimmedName,
        turnId,
        definition: trimmedDefinition,
        highlightedText,
      },
      {
        onSuccess: () => {
          setAnchorEl(null);
          reset();
        },
      },
    );
  };

  return (
    <>
      <Button
        size={size}
        startIcon={<AddCircleOutline fontSize="inherit" />}
        onClick={(evt) => setAnchorEl(evt.currentTarget)}
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          mt: 0.5,
          color: 'text.secondary',
          '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
        }}
      >
        Add code
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 360 }}>
          <Typography variant="subtitle2" gutterBottom>
            Add a code to this passage
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            Pick an existing code or type a name that doesn&apos;t exist yet to create one.
          </Typography>

          <Autocomplete
            size="small"
            value={value}
            onChange={(_evt, next) => {
              setValue(next);
              if (next && next.key === NEW_OPTION_KEY) {
                // Pre-fill the definition field if the user already
                // typed something descriptive in the input.
                setDefinition('');
              }
            }}
            inputValue={inputValue}
            onInputChange={(_evt, next) => setInputValue(next)}
            options={options}
            getOptionLabel={(option) => {
              if (!option) return '';
              if (option.key === NEW_OPTION_KEY) return option.inputValue || '';
              return option.name || '';
            }}
            isOptionEqualToValue={(option, val) =>
              option && val && option.key === val.key && option.name === val.name
            }
            filterOptions={(opts, params) => {
              const filtered = filter(opts, params);
              const trimmed = params.inputValue.trim();
              // Surface "Create new: foo" only when the input is non-empty
              // and doesn't exactly match an existing code (case-insensitive).
              if (trimmed) {
                const existingMatch = opts.some(
                  (o) => o.name.toLowerCase() === trimmed.toLowerCase(),
                );
                if (!existingMatch) {
                  filtered.unshift({
                    key: NEW_OPTION_KEY,
                    inputValue: trimmed,
                    label: `Create new: "${trimmed}"`,
                  });
                }
              }
              return filtered;
            }}
            renderOption={(props, option) => {
              if (option.key === NEW_OPTION_KEY) {
                return (
                  <li {...props} key={NEW_OPTION_KEY}>
                    <Stack>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        + Create new code: &quot;{option.inputValue}&quot;
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Add it to your codebook with a definition.
                      </Typography>
                    </Stack>
                  </li>
                );
              }
              return (
                <li {...props} key={option.name}>
                  <Stack>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.name}
                    </Typography>
                    {option.definition ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {option.definition}
                      </Typography>
                    ) : null}
                  </Stack>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Code name"
                placeholder="Search or type a new name…"
                // eslint-disable-next-line jsx-a11y/no-autofocus -- popover input, focus is expected on open
                autoFocus
              />
            )}
            freeSolo={false}
            clearOnBlur={false}
            handleHomeEndKeys
            sx={{ mb: 1.5 }}
          />

          {isCreateMode ? (
            <Box component="form" onSubmit={handleCreate}>
              <TextField
                label="Definition"
                size="small"
                fullWidth
                value={definition}
                onChange={(e) => setDefinition(e.target.value)}
                required
                multiline
                minRows={2}
                maxRows={5}
                placeholder="What does this code mean?"
                helperText="Required for new codes."
                sx={{ mb: 1.5 }}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button size="small" onClick={close} disabled={createMutation.isPending}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  type="submit"
                  disabled={!canCreate || createMutation.isPending}
                >
                  {createMutation.isPending ? 'Adding…' : 'Create and attach'}
                </Button>
              </Stack>
            </Box>
          ) : (
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={close} disabled={assignMutation.isPending}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleAssign}
                disabled={!canAssign || assignMutation.isPending}
              >
                {assignMutation.isPending ? 'Attaching…' : 'Attach code'}
              </Button>
            </Stack>
          )}
        </Box>
      </Popover>
    </>
  );
}
